import React, { FC, useState, useCallback, forwardRef, HTMLAttributes, CSSProperties } from 'react';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    DragOverlay,
    useSensor,
    useSensors,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from "@dnd-kit/utilities";
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { Button, Chip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ProTags } from './SortsDashboard';

type GridProps = {
    children: React.ReactNode;
    columns: number;
};
const Grid: FC<GridProps> = ({ children, columns }) => {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gridGap: 8,
                minWidth: '1100px',
                // margin: 'auto',
            }}
        >
            {children}
        </div>
    );
};

export type ItemProps = HTMLAttributes<HTMLDivElement> & {
    id: string;
    removeId: any,
    data: any;
    withOpacity?: boolean;
    isDragging?: boolean;
};
const Item = forwardRef<HTMLDivElement, ItemProps>(({ id, removeId, data, withOpacity, isDragging, style, ...props }, ref) => {
    const { P_Images, P_BrandNameEN, P_BrandName, P_CommonTitleEN, P_CommonTitle, ProductCountry_id, ProductCompany_id, P_ProContent, P_ProNum, P_SmallUnit, P_LimitNumMin, P_IsShelves, P_Reservation, P_IsHFGM, ISHuiZhen, Cate2, P_TransportType, P_Price, P_Application } = data;
    const inlineStyles: CSSProperties = {
        opacity: (withOpacity || !P_IsShelves) ? '0.5' : '1',
        // transformOrigin: '50% 50%',
        padding: '0 8px',
        height: '110px',
        // width: '33.33%',
        borderRadius: '10px',
        wordBreak: 'break-all',
        cursor: isDragging ? 'grabbing' : 'grab',
        backgroundColor: '#ffffff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: isDragging ? 'rgb(63 63 68 / 5%) 0px 2px 0px 2px, rgb(34 33 81 / 15%) 0px 2px 3px 2px' : 'rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(34 33 81 / 15%) 0px 1px 3px 0px',
        transform: isDragging ? 'scale(1.05)' : 'scale(1)',
        ...style,
    };
    const imgSrc = (P_Images != null && P_Images.length > 0) ? P_Images[0].src : '#';
    // return <div ref={ref} style={inlineStyles} {...props}>{id}</div>;
    return <Stack ref={ref} style={inlineStyles} {...props}>
        <Stack direction={'row'} alignItems={'center'} sx={{ gap: 1, width: '100%' }} >
            <Stack sx={{ fontSize: 12, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0 }}>{id}</div>
                <img style={{ width: 80, height: 80 }} src={imgSrc} />
            </Stack>
            <Stack sx={{ fontSize: 12, flex: 1 }}>
                <Stack direction={'row'} sx={{ gap: 1 }} alignItems={'center'}>
                    <div>{((P_LimitNumMin ?? 0) > 1) && <span style={{ color: 'red' }}>起购{P_LimitNumMin ?? 0}</span>}{P_BrandNameEN}</div>
                    <ProTags price={P_Price} P_IsShelves={P_IsShelves} P_TransportType={P_TransportType} hzgm={ISHuiZhen} hfgm={P_IsHFGM} reservation={P_Reservation} />
                </Stack>
                <div>{P_BrandName}</div>
                {/* <div>{P_CommonTitleEN}</div> */}
                <div>{P_CommonTitle}</div>
                <Stack direction={'row'} sx={{ gap: 1 }}>
                    <div>{P_ProContent}</div>
                    <div>{P_ProNum}/{P_SmallUnit}</div>
                </Stack>
            </Stack>
            {/* <Stack sx={{ fontSize: 14, maxWidth: 80 }} alignItems={'flex-end'}></Stack> */}
            <Stack>
                <Button data-no-dnd onClick={(e) => { removeId(id); e.stopPropagation(); return false; }}><CloseIcon /></Button>
            </Stack>
        </Stack>
        <Stack sx={{ fontSize: 12, width: '100%', height: 16, overflow: 'hidden' }}>
            {P_Application}
        </Stack>
    </Stack>
});

const SortableItem: FC<ItemProps> = (props) => {
    const {
        isDragging,
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
    };

    return (
        <Item
            ref={setNodeRef}
            style={style}
            withOpacity={isDragging}
            {...props}
            {...attributes}
            {...listeners}
        />
    );
};

// 1. 定义一个自定义鼠标传感器
class MyMouseSensor extends MouseSensor {
    static activators = [
        {
            eventName: 'onMouseDown' as const,
            handler: ({ nativeEvent: event }: { nativeEvent: MouseEvent }) => {
                // 如果点击的是带有 data-no-dnd 属性的元素或其子元素，则不激活拖拽
                const target = event.target as HTMLElement;
                if (target.closest('[data-no-dnd]')) {
                    return false;
                }
                return true;
            },
        },
    ];
}

const SortsDashboard_sub = ({ items, setItems }: { items: any[], setItems: any }) => {
    // const [items, setItems] = useState(Array.from({ length: 20 }, (_, i) => (i + 1).toString()));
    const [activeId, setActiveId] = useState<string | null>(null);
    const sensors = useSensors(useSensor(MyMouseSensor, {
        activationConstraint: {
            delay: 200, tolerance: 5
        }
    }), useSensor(TouchSensor));

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id.toString());
    }, []);
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        console.log('in handleDragEnd', active, over);
        if (over == null) {

        }
        else if (active.id !== over?.id) {
            setItems((items: any[]) => {
                try {
                    const oldIndex = items.findIndex(t => t.id == active.id.toString());
                    const newIndex = items.findIndex(t => t.id == over!.id.toString());
                    return arrayMove(items, oldIndex, newIndex);
                } catch {
                    alert('出现异常，请重新尝试');
                    return items;
                }
            });
        }
        setActiveId(null);
    }, []);
    const handleDragCancel = useCallback(() => {
        setActiveId(null);
    }, []);
    const removeId = (id: any) => {
        console.log('removeId', id);
        const _items = [...items];
        _items.splice(_items.findIndex(t => t.id == id), 1);
        setItems(_items);
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext items={items} strategy={rectSortingStrategy}>
                <Grid columns={4}>
                    {items.map((data: any) => (
                        <SortableItem key={data.id} id={data.id} data={data} removeId={removeId} />
                    ))}
                </Grid>
            </SortableContext>
            <DragOverlay adjustScale style={{ transformOrigin: '0 0 ' }}>
                {activeId ? <Item data={items.find(t => t.id == activeId)} id={activeId} removeId={removeId} isDragging /> : null}
            </DragOverlay>
        </DndContext>
    );
};

export default SortsDashboard_sub;